import {AppapproDTO} from "@/shared/api/model";

/**
 * Apply installment rounding based on the configured decimal precision.
 * @param amount The raw installment amount
 * @param precision Number of decimal places (0 = whole number, 2 = 2dp). Defaults to 2.
 */
export const roundInstallment = (amount: number, precision?: number | null): number => {
    const normalizedPrecision = precision ?? 2;
    const factor = 10 ** normalizedPrecision;

    return Math.ceil(amount * factor) / factor;
};

/**
 * Hook to prepare data for the deduction form from loan data and schedule
 * @param valueData The loan application data
 * @param scheduleData The loan schedule data
 * @param installmentRoundingPrecision Number of decimal places for installment rounding (from product config)
 * @returns Prepared data for the deduction form
 */
export const useDeductionFormData = (
    valueData?: AppapproDTO,
    scheduleData?: any,
    installmentRoundingPrecision?: number | null,
    feeBreakdown?: { totalRepaymentFees?: number }
) => {
    if (!valueData) {
        return {
            loanAmount: 0,
            loanPeriod: 0,
            interestRate: 0,
            installmentAmount: 0,
            maturityDate: null,
            disbursementDate: null,
            firstInstallmentDate: null,
        };
    }

    // Get the first installment amount from the schedule and apply configured rounding
    const scheduleEntries = scheduleData?.scheduleDTO || [];
    const firstInstallmentDate = scheduleEntries.length > 0 ? scheduleEntries[0]?.trxDate : null;
    const rawInstallment = scheduleEntries[0]?.totalDue || 0;
    const installmentAmount = roundInstallment(rawInstallment, installmentRoundingPrecision);

    // Get the maturity date from the last schedule entry
    const maturityDate = scheduleEntries.length > 0
        ? scheduleEntries[scheduleEntries.length - 1]?.trxDate
        : valueData?.maturityDate;

    // Use recommended values if available, otherwise use original values
    const loanAmount = valueData?.recommendedLoanAmt || valueData?.loanAmount || 0;
    const loanPeriod = valueData?.recommendedLnPeriod || valueData?.lnPeriod || 0;
    const interestRate = valueData?.intRate || 0;
    const disbursementDate = valueData?.disbursementDate;

    // totalLoanAmount = principal + interest + repayment fees (provided by backend on scheduleData, or computed from schedule + fees)
    const backendTotalLoanAmount = scheduleData?.totalLoanAmount ?? 0;
    const totalInterest = scheduleData?.totalInterest ?? 0;
    const scheduleFees =
        (scheduleData?.applicationFee ?? 0) +
        (scheduleData?.processingFee ?? 0) +
        (scheduleData?.commissionFee ?? 0) +
        (scheduleData?.commitmentFee ?? 0);
    const repaymentFees = feeBreakdown?.totalRepaymentFees ?? 0;
    const totalLoanAmount: number = backendTotalLoanAmount > 0
        ? backendTotalLoanAmount
        : loanAmount + totalInterest + scheduleFees + repaymentFees;

    return {
        loanAmount,
        loanPeriod,
        interestRate,
        installmentAmount,
        totalLoanAmount,
        maturityDate,
        disbursementDate,
        firstInstallmentDate,
    };
};
